import { useQueryClient } from '@tanstack/react-query';
import { useDeleteOrderBooking } from '@my/tms-api';
import { cn, ConfirmDialog, showToast, Switch } from '@my/ui';
import { Check, Ship, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFerryBookingFlow } from '../../ferry/flow';
import { ferryBookingAccepted } from '../../ferry/status';
import { buildBookedSailing, buildBookingDetail, buildJourney } from '../lib/bookingDetail';
import { STATUS_BADGE, STATUS_DOT } from '../lib/format';
import type { BookingRow } from '../lib/mockData';
import { Dot, JourneyProgress, KV, ReqChip, Section } from './bookingPanelBits';
import { SailingTypePill } from './SailingTypePill';

/**
 * Expand-down booking screen for a BOOKED row — the booking as the TMS and the
 * carrier hold it (sailing, reference, status, unit, customs) with the journey
 * laid out as a horizontal progress bar along the bottom (done = green,
 * upcoming = grey). Delete cancels at the carrier first, then removes the
 * booking; changes are made on the order page.
 */
export function BookingDetails({ row, onClose }: { row: BookingRow; onClose: () => void }) {
  const detail = useMemo(() => buildBookingDetail(row), [row]);
  const sailing = useMemo(() => buildBookedSailing(row, detail.ctx), [row, detail.ctx]);
  const journey = useMemo(() => buildJourney(sailing, detail.ctx), [sailing, detail.ctx]);

  const booking = row.booking ?? null;
  const ferry = row.ferry ?? null;
  const ferryAccepted = !!ferry && ferryBookingAccepted(ferry);

  const qc = useQueryClient();
  const ferryFlow = useFerryBookingFlow();
  const deleteBooking = useDeleteOrderBooking();
  const [pendingDelete, setPendingDelete] = useState(false);
  // Default on: the legacy service always kept the block for Stena.
  const [keepCapacity, setKeepCapacity] = useState(true);
  const busy = deleteBooking.isPending || ferryFlow.isBusy;

  // The vehicle box as the design has it; changes are made on the order page.
  const [vehicle, setVehicle] = useState(booking?.vehicleRegistration ?? '');

  const handleSave = () => {
    showToast({ type: 'success', message: `Booking ${row.orderNumber} updated.` });
    onClose();
  };

  const confirmDelete = async () => {
    if (!booking) return;
    // A booking made through the carrier is only deleted once its carrier side
    // has been read, so the cancel is never skipped while the lookup is
    // loading or failing.
    if (booking.ferryBookingId && !ferry) {
      showToast({
        type: 'error',
        message: 'The carrier booking could not be read; try again before deleting.',
      });
      return;
    }
    try {
      // The carrier first; if it refuses, nothing is deleted.
      if (ferry) {
        const refusal = await ferryFlow.cancelAtFerry({ ferryBooking: ferry, keepCapacity });
        if (refusal) {
          showToast({ type: 'error', message: `${refusal} The booking was kept.` });
          return;
        }
      }
      await deleteBooking.mutateAsync({ orderId: row.id, bookingId: booking.id });
      qc.invalidateQueries({ queryKey: ['tms', 'orderBooking'] });
      showToast({
        type: 'success',
        message: ferryAccepted
          ? `Cancelled with ${sailing?.operator || 'the carrier'} and deleted.`
          : `Booking ${row.orderNumber} deleted.`,
      });
      onClose();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to delete booking.',
      });
    } finally {
      setPendingDelete(false);
    }
  };

  // The unit as booked: the TMS plates, the carrier's figures for the type it holds.
  const dims = ferry ? `${ferry.lengthMetres} × ${ferry.widthMetres} × ${ferry.heightMetres} m` : '—';
  const weight = ferry ? `${(ferry.vehicleWeightKg / 1000).toFixed(1)} t` : '—';
  const drivers = booking?.numberOfDrivers ?? 1;
  const accompanied = !booking
    ? '—'
    : booking.isAccompanied
      ? `Yes · ${drivers} driver${drivers === 1 ? '' : 's'}`
      : 'No';

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-slate-800 px-3 py-2 text-xs text-slate-200">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-emerald-500/90 text-white">
          <Ship className="h-3 w-3" aria-hidden="true" />
        </span>
        <span className="font-semibold text-white">Ferry Booking</span>
        <span className="font-semibold text-emerald-400">{row.orderNumber}</span>
        <Dot />
        <span className="text-slate-100">{row.customer}</span>
        <Dot />
        <span>{row.corridor}</span>
        <Dot />
        <span>{detail.routeLabel}</span>
        <Dot />
        <span>
          Deliver {detail.ctx.deliveryDateLabel} · {row.window}
        </span>
        {detail.customs && (
          <span className="rounded bg-amber-400/90 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-900">
            Customs
          </span>
        )}

        <span
          className={cn(
            'ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset',
            STATUS_BADGE[row.status],
          )}
        >
          <span className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[row.status])} aria-hidden="true" />
          {row.status}
        </span>
      </div>

      {/* ── Body — 3 columns: unit/movement (1/4) · booking (2/4) · docs (1/4) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_1fr]">
        {/* Left 1/4 — unit + movement */}
        <aside className="space-y-4 border-b border-slate-200 p-3 text-xs lg:border-b-0 lg:border-r">
          <Section title="Unit">
            {/* Vehicle is editable; the rest read from the booking. */}
            <div className="flex items-center justify-between gap-2 py-0.5">
              <span className="text-[11px] text-slate-400">Vehicle</span>
              <input
                value={vehicle}
                onChange={(e) => setVehicle(e.target.value)}
                aria-label="Vehicle"
                className="w-40 rounded-sm border border-slate-300 bg-white px-2 py-0.5 text-right text-[11px] text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <KV k="Trailer" v={booking?.trailerNumber || '—'} />
            <KV k="Dimensions" v={dims} />
            <KV k="Weight" v={weight} />
          </Section>

          <Section title="Movement">
            <KV k="Accompanied" v={accompanied} />
            <KV k="Contact" v={detail.movement.contact} />
            <KV k="Haulier type" v={detail.movement.haulier} />
          </Section>
        </aside>

        {/* Centre 2/4 — the booked sailing */}
        <div className="min-w-0 border-b border-slate-200 p-3 lg:border-b-0">
          <Section title="Booking">
            {sailing ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">{sailing.routeLabel}</span>
                  <SailingTypePill type={sailing.type} />
                </div>
                <div className="rounded-md border border-slate-200">
                  <KVRow k="Reference">
                    {row.bookingRef ? (
                      <span className="font-mono text-slate-700">{row.bookingRef}</span>
                    ) : (
                      <span className="italic text-slate-400">Awaiting confirmation</span>
                    )}
                  </KVRow>
                  <KVRow k="Operator">{sailing.operator}</KVRow>
                  <KVRow k="Sailing">
                    <span className="tabular-nums">
                      {sailing.departs} → {sailing.arrives}
                    </span>{' '}
                    <span className="text-slate-400">· {sailing.duration}</span>
                  </KVRow>
                  <KVRow k="Road to dest" last>
                    {sailing.roadToDest}
                  </KVRow>
                </div>
                {ferry?.message && (
                  <p className="text-[11px] font-medium text-red-600">{ferry.message}</p>
                )}
              </div>
            ) : (
              <p className="text-[11px] italic text-slate-400">No sailing on this booking.</p>
            )}
          </Section>
        </div>

        {/* Right 1/4 — requirements + customs + collection */}
        <aside className="space-y-4 p-3 text-xs lg:border-l lg:border-slate-200">
          <Section title="Booking requirements">
            <div className="space-y-1">
              {detail.requirements.map((r) => (
                <ReqChip key={r.label} req={r} />
              ))}
            </div>
          </Section>

          {detail.customsDocs && (
            <Section title="Customs documents">
              <p className="mb-1.5 text-[11px] text-slate-500">{detail.customsDocs.note}</p>
              <KV k="PBN" v={ferry?.exportId || '—'} />
              <KV k="GMR" v={ferry?.importId || '—'} />
            </Section>
          )}

          <Section title="Collection to port">
            <KV k="Road time" v={detail.collection.roadTime} />
            <KV k="Latest at port" v={journey.latestAtPort} amber />
            <KV k="Delivery window" v={row.window} red />
          </Section>
        </aside>
      </div>

      {/* ── Journey progress (bottom) ──────────────────────────────────────── */}
      <JourneyProgress steps={journey.steps} />

      {/* ── Footer — Delete (left) · Cancel · Save ─────────────────────────── */}
      <div className="flex items-center gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2 text-xs">
        <button
          type="button"
          aria-label="Delete booking"
          title="Delete booking"
          onClick={() => setPendingDelete(true)}
          disabled={busy || !booking}
          className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>

        {sailing && (
          <span className="text-slate-500">
            <span className="font-semibold tabular-nums text-slate-700">
              {sailing.departs} to {sailing.arrives}
            </span>
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            aria-label="Cancel"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="Save"
            onClick={handleSave}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete}
        title="Delete booking?"
        message={
          ferryAccepted ? (
            <div className="space-y-3">
              <p>
                The booking is cancelled with {sailing?.operator || 'the carrier'} first, then
                removed with both paired tasks (Boarding and Disembark). This can't be undone.
              </p>
              <Switch
                label="Keep the space at the carrier"
                enabledLabel="Yes, keep the block"
                disabledLabel="No, release it"
                checked={keepCapacity}
                onChange={(e) => setKeepCapacity(e.target.checked)}
              />
            </div>
          ) : (
            "The booking and both paired tasks (Boarding and Disembark) will be removed. This can't be undone."
          )
        }
        confirmLabel="Delete"
        tone="danger"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(false)}
        busy={busy}
      />
    </div>
  );
}

/** One labelled row inside the Booking summary card. */
function KVRow({ k, children, last }: { k: string; children: React.ReactNode; last?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3 px-2.5 py-1.5 text-[11px]',
        !last && 'border-b border-slate-100',
      )}
    >
      <span className="text-slate-400">{k}</span>
      <span className="text-right text-slate-700">{children}</span>
    </div>
  );
}
